import React, { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { BRAND } from '../brand';
import { countryName } from '../../shared/countries';
import { TERMS_VERSION, OPERATOR, DEFAULT_MIN_AGE, ageTable } from '../../shared/legal';

export type LegalDoc = 'terminos' | 'privacidad';

/** Documento legal abierto por su dirección (`/terminos`, `/privacidad`). */
export function legalDocFromPath(): LegalDoc | null {
  const m = /^\/(terminos|privacidad)\/?$/.exec(location.pathname);
  return m ? (m[1] as LegalDoc) : null;
}

/**
 * Términos de uso y Política de privacidad.
 *
 * Están escritos para que los lea una persona, no un juzgado: frases cortas,
 * qué se hace y qué no. La tabla de edades y la versión salen de
 * shared/legal.ts, que es lo mismo que aplica el servidor, para que la página
 * no prometa una cosa y el registro haga otra.
 *
 * No sustituye la revisión de un abogado: es una base honesta que describe lo
 * que el código hace de verdad.
 */
export const LegalPage: React.FC<{
  doc: LegalDoc;
  onNavigate: (doc: LegalDoc) => void;
  onBack: () => void;
}> = ({ doc, onNavigate, onBack }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [doc]);

  const fecha = new Date(`${TERMS_VERSION}T00:00:00`).toLocaleDateString('es', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <article className="max-w-3xl mx-auto px-4 py-8 sm:py-12">
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-ink-400 hover:text-ink-100 mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Volver
      </button>

      <div role="tablist" className="flex gap-1 p-1 rounded-lg bg-ink-850 border border-ink-700 w-fit mb-8">
        {(['terminos', 'privacidad'] as const).map((d) => (
          <button
            key={d}
            role="tab"
            aria-selected={doc === d}
            onClick={() => onNavigate(d)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              doc === d ? 'bg-ink-700 text-ink-50' : 'text-ink-400 hover:text-ink-100'
            }`}
          >
            {d === 'terminos' ? 'Términos de uso' : 'Privacidad'}
          </button>
        ))}
      </div>

      <div className="legal">
        {doc === 'terminos' ? <Terminos fecha={fecha} /> : <Privacidad fecha={fecha} />}
      </div>
    </article>
  );
};

const Contacto: React.FC = () => (
  <a href={`mailto:${OPERATOR.email}`}>{OPERATOR.email}</a>
);

/* ------------------------------------------------------------------ *
 * Términos de uso
 * ------------------------------------------------------------------ */

const Terminos: React.FC<{ fecha: string }> = ({ fecha }) => (
  <>
    <h1>Términos de uso</h1>
    <p className="legal-meta">Versión del {fecha}</p>

    <p>
      Estos términos son el acuerdo entre tú y {OPERATOR.name} (en adelante, «nosotros»),
      responsable de {BRAND.name}. Al crear una cuenta los aceptas expresamente. Si juegas sin
      cuenta, como invitado, se aplican igualmente mientras uses el servicio.
    </p>

    <h2>1. Qué es {BRAND.name}</h2>
    <p>
      Una plataforma gratuita de trivias en vivo: puedes crear cuestionarios, abrir una sala con
      PIN, jugar con otras personas y practicar por tu cuenta. No hay planes de pago ni compras
      dentro del servicio.
    </p>

    <h2>2. Edad y cuentas</h2>
    <ul>
      <li>
        Para jugar como invitado no hace falta cuenta ni dar datos personales más allá de un
        apodo.
      </li>
      <li>
        Para tener cuenta necesitas la edad mínima que fija la ley de tu país ({DEFAULT_MIN_AGE}{' '}
        años salvo las excepciones que lista la <a href="/privacidad">Política de privacidad</a>).
        En algunos países se admite una cuenta por debajo de esa edad con el permiso expreso de la
        madre, el padre o el tutor legal, que acepta estos términos en nombre del menor.
      </li>
      <li>
        Los datos que das al registrarte tienen que ser ciertos, en especial la fecha de nacimiento
        y el país, porque de ellos depende qué edad mínima se te aplica. Si descubrimos que una
        cuenta no cumple la edad mínima, la cerraremos y borraremos sus datos.
      </li>
      <li>
        Eres responsable de lo que se haga con tu cuenta: guarda tu contraseña y no la compartas.
      </li>
    </ul>

    <h2>3. Los {BRAND.currencyName}</h2>
    <p>
      Los {BRAND.currencyName} son puntos internos del juego.{' '}
      <strong>
        No son dinero, no son una criptomoneda, no tienen valor económico y no se pueden comprar,
        vender, cambiar por dinero ni por ningún otro bien, ni retirar.
      </strong>{' '}
      No son de tu propiedad: son una licencia de uso dentro del juego que podemos ajustar, corregir
      o reiniciar, por ejemplo si se obtuvieron con trampas o por un error.
    </p>
    <p>
      Los «botes» que un anfitrión pone en su partida son {BRAND.currencyName} y se rigen por lo
      mismo: no se apuesta nada con valor real y nadie puede ganar ni perder dinero en {BRAND.name}.
    </p>

    <h2>4. Billetera cripto (opcional)</h2>
    <p>
      Puedes vincular una billetera solo para demostrar que esa dirección es tuya, firmando un
      mensaje. Nunca pedimos tu frase semilla ni tu clave privada, no custodiamos fondos y la firma
      no autoriza ninguna transacción. Esta función no está disponible para menores.
    </p>

    <h2>5. Tu contenido</h2>
    <ul>
      <li>
        Las trivias que creas son tuyas. Al publicarlas nos das permiso, gratuito, mundial y
        mientras no las borres, para guardarlas, mostrarlas y dejar que otras personas las jueguen
        según la visibilidad que elijas (pública, solo con enlace o privada).
      </li>
      <li>
        Solo puedes subir contenido sobre el que tengas derechos. Si generas una trivia a partir de
        un PDF, asegúrate de que puedes usar ese documento así.
      </li>
      <li>
        Las preguntas generadas con IA pueden contener errores. Revísalas antes de publicarlas:
        el contenido que publicas es responsabilidad tuya.
      </li>
    </ul>

    <h2>6. Normas de uso</h2>
    <p>No está permitido:</p>
    <ul>
      <li>usar nombres, apodos, avatares o contenido ofensivo, discriminatorio, sexual o violento;</li>
      <li>acosar, amenazar o suplantar a otras personas;</li>
      <li>publicar datos personales de terceros;</li>
      <li>hacer trampas, automatizar partidas o aprovechar errores del sistema;</li>
      <li>atacar, sobrecargar o intentar acceder sin permiso al servicio o a otras cuentas;</li>
      <li>usar {BRAND.name} para cualquier actividad ilegal.</li>
    </ul>
    <p>
      Podemos retirar contenido y suspender o cerrar cuentas que incumplan estas normas. Siempre
      que sea posible lo avisaremos y explicaremos el motivo.
    </p>

    <p>
      <strong>Anfitriones que supervisan salas.</strong> Si activas la supervisión de una sala,
      aceptas que: (a) tienes derecho a hacerla y, si juegan menores, cuentas con el permiso de sus
      tutores o del centro; (b) solo usarás las grabaciones para detectar trampas en esa partida y
      no las compartirás, publicarás ni conservarás fuera de {BRAND.name}; (c) atenderás a quien
      pida que se borren; y (d) la función puede retirarse a quien la use mal.
    </p>

    <h2>7. Denuncias de contenido y derechos de autor</h2>
    <p>
      Si ves contenido que infringe estas normas o tus derechos de autor, escríbenos a{' '}
      <Contacto /> indicando el enlace de la trivia, qué infringe y, si es tuyo, que eres titular de
      los derechos. Lo revisaremos y retiraremos lo que corresponda.
    </p>

    <h2>8. Generación con IA</h2>
    <p>
      Para generar trivias con IA usas tu propia clave de Google AI Studio. Lo que envíes (el tema
      o el texto del PDF) va a Google con esa clave y bajo sus condiciones; los límites y posibles
      costes de esa clave son tuyos.
    </p>

    <h2>9. Disponibilidad y responsabilidad</h2>
    <p>
      {BRAND.name} es gratuito y se ofrece «tal cual». Hacemos lo posible para que funcione bien,
      pero no podemos garantizar que esté siempre disponible ni libre de errores. En la medida en
      que la ley lo permita, no respondemos de daños indirectos derivados de su uso. Nada de esto
      limita los derechos que te reconozca como consumidor la ley de tu país.
    </p>

    <h2>10. Cerrar tu cuenta</h2>
    <p>
      Puedes borrar tu cuenta cuando quieras desde tu perfil (Editar perfil → Privacidad). Se borran
      tus datos y tus trivias, como explica la Política de privacidad.
    </p>

    <h2>11. Cambios</h2>
    <p>
      Si cambiamos estos términos de forma importante, te pediremos que los aceptes de nuevo al
      entrar. Si no estás de acuerdo, puedes borrar tu cuenta.
    </p>

    <h2>12. Ley aplicable</h2>
    <p>
      Estos términos se rigen por la ley de {OPERATOR.country}, sin perjuicio de las normas de
      protección al consumidor y de datos de tu país de residencia que no se puedan dejar de
      aplicar por contrato.
    </p>

    <h2>13. Contacto</h2>
    <p>
      {OPERATOR.name} · <Contacto />
    </p>
  </>
);

/* ------------------------------------------------------------------ *
 * Política de privacidad
 * ------------------------------------------------------------------ */

const Privacidad: React.FC<{ fecha: string }> = ({ fecha }) => {
  const edades = ageTable().sort((a, b) =>
    (countryName(a.code) ?? a.code).localeCompare(countryName(b.code) ?? b.code, 'es'),
  );

  return (
    <>
      <h1>Política de privacidad</h1>
      <p className="legal-meta">Versión del {fecha}</p>

      <p>
        Aquí explicamos qué datos guarda {BRAND.name}, para qué, con quién se comparten y qué puedes
        hacer con ellos. La regla general: guardamos lo necesario para que el juego funcione y nada
        para publicidad. No vendemos datos a nadie.
      </p>

      <h2>1. Responsable</h2>
      <p>
        {OPERATOR.name}, {OPERATOR.country}. Para cualquier asunto de privacidad: <Contacto />.
      </p>

      <h2>2. Qué datos guardamos</h2>
      <h3>Si juegas como invitado</h3>
      <ul>
        <li>El apodo y el avatar que elijas, y los resultados de tus partidas.</li>
        <li>
          Se borran automáticamente a los 12 meses de tu última partida. Tu navegador olvida tu
          identidad de invitado al cerrarse.
        </li>
      </ul>

      <h3>Si tienes cuenta</h3>
      <ul>
        <li>
          <strong>Datos de la cuenta:</strong> usuario, nombre y apellido, fecha de nacimiento,
          país, contraseña (guardada solo como hash con scrypt, nunca en claro) y, si entras con
          Google, tu correo y tu identificador de Google.
        </li>
        <li>
          <strong>Datos de juego:</strong> avatar, estadísticas, D-Rating, historial de partidas,
          movimientos de {BRAND.currencyName}, logros, favoritas y las trivias que creas.
        </li>
        <li>
          <strong>Opcionales:</strong> la dirección pública de tu billetera si la vinculas, y tu
          clave de Google AI Studio si la guardas (cifrada), con un recuento de cuántas veces la
          usas.
        </li>
        <li>
          <strong>Constancia legal:</strong> qué versión de estos documentos aceptaste y cuándo, y,
          si aplica, cuándo dio su permiso tu madre, padre o tutor.
        </li>
        <li>
          <strong>Técnicos:</strong> tu sesión (un código aleatorio que caduca a los 30 días) y,
          temporalmente, la dirección IP de tus peticiones para frenar abusos como los intentos
          masivos de contraseña. El servidor web puede conservar registros técnicos con la IP
          durante un tiempo limitado por seguridad.
        </li>
      </ul>

      <h3>Si entras en una sala supervisada</h3>
      <ul>
        <li>
          Algunas salas las abre un anfitrión con la supervisión anti-trampas activada. Se anuncia
          en la lista de salas, y antes de entrar se te pide tu permiso expreso en una pantalla
          aparte. <strong>Si no aceptas, no se graba nada</strong> y sales de la sala sin
          consecuencias.
        </li>
        <li>
          <strong>Qué se graba:</strong> lo que ocurre dentro de la pestaña de {BRAND.name}
          (clics, movimientos y cambios en la pantalla; los campos de texto se enmascaran) y
          cuántas veces sales de la pestaña, pierdes el foco, copias o pegas. <strong>No se graba</strong>{' '}
          nada fuera de la pestaña: ni otras pestañas, ni otras aplicaciones, ni cámara ni
          micrófono.
        </li>
        <li>
          <strong>Quién la ve:</strong> solo el anfitrión que abrió la sala, en directo y después
          desde su cuenta. Guardamos además cuándo aceptaste, como constancia de tu permiso.
        </li>
        <li>
          <strong>Cuánto tiempo:</strong> se borra automáticamente a los 30 días, y antes si pides
          su supresión o eliminas tu cuenta.
        </li>
        <li>
          <strong>Menores:</strong> a las cuentas de menores de 18 años, o sin fecha de nacimiento, no se les graba. Quien juega
          como invitado confirma al aceptar que tiene edad para decidirlo o que su madre, padre o
          tutor le ha dado permiso.
        </li>
        <li>
          <strong>Quién es responsable:</strong> {OPERATOR.name} aporta la herramienta y la custodia
          en el servidor; el anfitrión decide activarla y es responsable de tener un motivo legítimo
          para hacerlo y de usar las grabaciones solo para detectar trampas en esa partida.
        </li>
      </ul>

      <h2>3. Qué ven los demás</h2>
      <ul>
        <li>
          <strong>Público:</strong> tu usuario, tu avatar, tus estadísticas de juego, tu país, tu
          actividad y las trivias que publiques como públicas.
        </li>
        <li>
          <strong>Nunca público:</strong> tu nombre real, tu correo, tu fecha de nacimiento, tu
          contraseña, tu saldo y tu clave de IA.
        </li>
        <li>
          Los perfiles de menores con cuenta no son visibles para otros jugadores ni aparecen en el
          buscador.
        </li>
      </ul>

      <h2>4. Para qué los usamos y con qué base legal</h2>
      <ul>
        <li>
          <strong>Prestarte el servicio</strong> (cuenta, partidas, ranking, trivias): es necesario
          para cumplir lo que acordamos en los Términos.
        </li>
        <li>
          <strong>Cumplir la ley</strong>: la fecha de nacimiento y el país sirven para aplicar la
          edad mínima de tu país.
        </li>
        <li>
          <strong>Seguridad y prevención de abusos</strong>: interés legítimo en proteger el
          servicio y a quienes lo usan.
        </li>
        <li>
          <strong>Grabación en salas supervisadas</strong>: tu consentimiento expreso, previo a
          cada sala, que retiras saliendo de ella; lo ya grabado se borra si lo pides.
        </li>
        <li>
          <strong>Funciones opcionales</strong> (billetera, clave de IA): tu consentimiento, que
          retiras desvinculando la billetera o borrando la clave.
        </li>
      </ul>
      <p>No usamos tus datos para publicidad, no hacemos perfiles comerciales y no los vendemos.</p>

      <h2>5. Con quién se comparten</h2>
      <ul>
        <li>
          <strong>Google</strong>, solo si entras con Google (para identificarte) o si generas
          trivias con IA con tu clave (el tema o el texto del PDF se envían a Google AI Studio).
        </li>
        <li>
          <strong>El proveedor de alojamiento</strong> del servidor, que guarda la base de datos por
          cuenta nuestra.
        </li>
        <li>
          <strong>El administrador de la plataforma</strong> puede consultar las claves de IA
          guardadas y su uso para supervisar abusos. Si no quieres que esto ocurra, no guardes tu
          clave.
        </li>
        <li>Autoridades, solo cuando una ley nos obligue.</li>
      </ul>
      <p>
        Las fuentes tipográficas se sirven desde nuestro propio servidor: al visitar {BRAND.name}
        tu navegador no se conecta a servicios de terceros.
      </p>

      <h2>6. Cookies y almacenamiento del navegador</h2>
      <p>
        No usamos cookies de publicidad ni de analítica. El navegador guarda tu sesión y el tema
        elegido (localStorage) y, si juegas como invitado, tu apodo hasta que lo cierres
        (sessionStorage). Son imprescindibles para que el sitio funcione, por eso no pedimos un
        consentimiento aparte.
      </p>

      <h2>7. Cuánto tiempo</h2>
      <ul>
        <li>Cuenta: mientras exista. Al borrarla se borra todo, incluidas tus trivias.</li>
        <li>Sesiones: 30 días como máximo.</li>
        <li>Invitados: 12 meses desde su última partida.</li>
      </ul>

      <h2>8. Tus derechos</h2>
      <p>
        Según tu país (RGPD en la Unión Europea, LGPD en Brasil, CCPA en California, Ley 172-13 en
        República Dominicana y otras leyes de datos) tienes derecho a:
      </p>
      <ul>
        <li>
          <strong>Acceder y llevarte tus datos:</strong> Editar perfil → Privacidad → «Descargar mis
          datos».
        </li>
        <li>
          <strong>Corregirlos:</strong> desde Editar perfil. La fecha de nacimiento no se puede
          cambiar desde la web porque de ella depende la edad mínima: escríbenos si está mal.
        </li>
        <li>
          <strong>Borrarlos:</strong> Editar perfil → Privacidad → «Borrar mi cuenta».
        </li>
        <li>
          <strong>Oponerte o limitar</strong> un tratamiento y <strong>retirar tu consentimiento</strong>{' '}
          en las funciones opcionales.
        </li>
        <li>
          <strong>Reclamar</strong> ante la autoridad de protección de datos de tu país.
        </li>
      </ul>
      <p>
        Para cualquier otra petición escríbenos a <Contacto />. Responderemos en un plazo máximo de
        30 días.
      </p>

      <h2>9. Menores</h2>
      <p>
        Para jugar como invitado no hace falta edad mínima ni datos personales. Para tener cuenta,
        la edad mínima es la que fija la ley de datos de tu país: {DEFAULT_MIN_AGE} años salvo en
        estos casos:
      </p>
      <div className="overflow-x-auto">
        <table>
          <thead>
            <tr>
              <th>País</th>
              <th>Edad mínima</th>
              <th>Con permiso del tutor</th>
            </tr>
          </thead>
          <tbody>
            {edades.map((e) => (
              <tr key={e.code}>
                <td>{countryName(e.code) ?? e.code}</td>
                <td>{e.alone} años</td>
                <td>{e.withGuardian !== null ? `desde ${e.withGuardian} años` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>
        Las cuentas de menores que su país admite con permiso del tutor no pueden vincular una
        billetera y su perfil no es visible para otros jugadores. Si eres madre, padre o tutor y
        quieres consultar o borrar los datos de un menor, escríbenos a <Contacto />. Si detectamos
        una cuenta de alguien sin la edad mínima, la borramos.
      </p>

      <h2>10. Seguridad</h2>
      <p>
        Las conexiones van cifradas (HTTPS), las contraseñas se guardan con scrypt, las claves de
        IA cifradas, y el acceso a la base de datos está restringido. Si hubiera una brecha que
        afectara a tus datos, te avisaríamos y lo notificaríamos a las autoridades como manda la
        ley.
      </p>

      <h2>11. Transferencias internacionales</h2>
      <p>
        El servidor puede estar en un país distinto del tuyo, y Google puede tratar datos fuera de
        él. Cuando eso ocurra se hará con las garantías que exige la ley aplicable.
      </p>

      <h2>12. Cambios</h2>
      <p>
        Si cambiamos esta política de forma importante, te pediremos que la aceptes de nuevo al
        entrar.
      </p>
    </>
  );
};
